import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Store, Wallet, CircleCheck, IndianRupee, Receipt } from "lucide-react";
import { getPayrollSummary, listPayrollHistory } from "../../api/payroll";
import { listBranches } from "../../api/branches";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import {
  Card, Select, EmptyState, TableSkeleton, StatCard, Skeleton, Avatar,
} from "../../components/ui";
import Pagination from "../../components/Pagination";
import {
  unwrapList, refLabel, formatDateTime, formatMonth, errorMessage, currentMonth, shiftMonth,
} from "../../lib/records";
import { formatMoney } from "../../lib/salary";
import { PAYMENT_MODE_LABELS, PAYROLL_STATUS_STYLES, PAYROLL_STATUS_LABELS } from "../../lib/enums";

const PAGE_SIZE = 10;

// §11.8 — what has actually been paid out versus what is still owed, across a
// range of months, plus the raw payment ledger underneath it.
//
// Important caveat the page states outright: this only covers months that have
// been *processed*. A month nobody has touched carries no row here — its
// liability lives in the payroll sheet (§11.4), which is the number to look at
// before payday.
export default function PayrollMoneyView() {
  const { partner } = useAuth();
  const isChain = partner?.kind === "CHAIN";

  const [months, setMonths] = useState(6);
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState("");

  const [ledger, setLedger] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: PAGE_SIZE, pages: 1 });
  const [page, setPage] = useState(1);
  const [ledgerLoading, setLedgerLoading] = useState(true);

  const to = currentMonth();
  const from = shiftMonth(to, -(months - 1));

  useEffect(() => {
    if (!isChain) return;
    listBranches({ limit: 100 })
      .then((res) => setBranches(unwrapList(res, 100).items))
      .catch(() => setBranches([]));
  }, [isChain]);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError("");
    try {
      const res = await getPayrollSummary({
        from,
        to,
        ...(isChain && branchId ? { partner: branchId } : {}),
      });
      setSummary(res?.data ?? null);
    } catch (err) {
      setSummary(null);
      setSummaryError(errorMessage(err, "The money view could not be loaded."));
    } finally {
      setSummaryLoading(false);
    }
  }, [from, to, branchId, isChain]);

  const fetchLedger = useCallback(async () => {
    setLedgerLoading(true);
    try {
      const res = await listPayrollHistory({
        page,
        limit: PAGE_SIZE,
        from,
        to,
      });
      const { items, pagination: meta } = unwrapList(res, PAGE_SIZE);
      setLedger(items);
      setPagination(meta);
    } catch {
      setLedger([]);
    } finally {
      setLedgerLoading(false);
    }
  }, [page, from, to]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchLedger();
  }, [fetchLedger]);

  useEffect(() => {
    setPage(1);
  }, [months, branchId]);

  const totals = summary?.totals;
  const byMonth = summary?.byMonth ?? [];
  const peak = Math.max(1, ...byMonth.map((m) => m.netPayable ?? 0));

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8">
        <Link
          to="/payroll"
          className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:underline transition-colors"
        >
          <ArrowLeft size={15} /> Back to payroll
        </Link>

        <PageHeader
          title="Paid & due"
          subtitle="What has gone out, and what is still owed, across recent months."
          actions={
            <div className="flex flex-wrap items-center gap-2">
              {isChain && (
                <div className="flex items-center gap-2">
                  <Store size={15} className="shrink-0 text-gray-400" />
                  <Select
                    value={branchId}
                    onChange={(e) => setBranchId(e.target.value)}
                    className="w-full sm:w-44 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                  >
                    <option value="">All branches</option>
                    {branches.map((b) => (
                      <option key={b._id} value={b._id}>
                        {b.name}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
              {/* The API caps this range at 24 months. */}
              <Select
                value={months}
                onChange={(e) => setMonths(Number(e.target.value))}
                className="w-36 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
              >
                <option value={3}>Last 3 months</option>
                <option value={6}>Last 6 months</option>
                <option value={12}>Last 12 months</option>
                <option value={24}>Last 24 months</option>
              </Select>
            </div>
          }
        />

        {summaryError && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
            {summaryError}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          {summaryLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Card key={i} className="p-3 sm:p-5 bg-white/80 backdrop-blur-sm border border-emerald-100">
                <Skeleton className="h-3 sm:h-4 w-24" />
                <Skeleton className="mt-2 sm:mt-3 h-6 sm:h-8 w-20" />
              </Card>
            ))
          ) : (
            <>
              <StatCard label="Net payable" value={formatMoney(totals?.netPayable ?? 0)} icon={Wallet} tone="primary" />
              <StatCard label="Paid out" value={formatMoney(totals?.paidAmount ?? 0)} icon={CircleCheck} tone="green" />
              <StatCard label="Still due" value={formatMoney(totals?.dueAmount ?? 0)} icon={IndianRupee} tone="orange" />
            </>
          )}
        </div>

        <Card className="mt-4 sm:mt-5 p-4 sm:p-5 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50">
          <h2 className="text-base sm:text-headline-sm font-semibold text-gray-800">By month</h2>
          <p className="text-xs sm:text-body-sm text-gray-500">
            {formatMonth(from)} — {formatMonth(to)}
          </p>

          {summaryLoading ? (
            <div className="mt-4 space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-9" />
              ))}
            </div>
          ) : byMonth.length === 0 ? (
            <div className="mt-2">
              <EmptyState
                icon={Wallet}
                title="No processed months yet"
                description="A month appears here once it has been adjusted or paid. Untouched months show in the payroll sheet."
              />
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {byMonth.map((m) => {
                const paidPct = m.netPayable ? ((m.paidAmount ?? 0) / m.netPayable) * 100 : 0;
                return (
                  <div key={m.month} className="flex items-center gap-3">
                    <span className="w-24 shrink-0 text-xs font-medium text-gray-700">
                      {formatMonth(m.month)}
                    </span>
                    <div
                      className="h-7 overflow-hidden rounded-md bg-gray-100"
                      style={{ width: `${Math.max(8, ((m.netPayable ?? 0) / peak) * 100)}%` }}
                      title={`${formatMoney(m.paidAmount)} paid of ${formatMoney(m.netPayable)}`}
                    >
                      <div
                        className="h-full bg-emerald-500 transition-all duration-500"
                        style={{ width: `${Math.min(100, paidPct)}%` }}
                      />
                    </div>
                    {/* A fully-settled month says so; only an outstanding
                        balance is worth calling out in amber. */}
                    <span className="shrink-0 text-xs text-gray-600">
                      {formatMoney(m.paidAmount ?? 0)}
                      <span className="text-gray-400"> / {formatMoney(m.netPayable ?? 0)}</span>
                      {(m.dueAmount ?? 0) > 0 ? (
                        <span className="ml-1.5 font-semibold text-amber-600">
                          {formatMoney(m.dueAmount)} due
                        </span>
                      ) : (
                        <span className="ml-1.5 font-medium text-emerald-600">settled</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* The raw ledger. Reversals appear as negative amounts, because
            paidAmount is always the sum of payments — nothing is deleted. */}
        <Card className="mt-4 sm:mt-5 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50 overflow-hidden">
          <div className="border-b border-emerald-100/60 p-4">
            <h2 className="flex items-center gap-2 text-base sm:text-headline-sm font-semibold text-gray-800">
              <Receipt size={17} className="text-emerald-600" /> Payment ledger
            </h2>
            <p className="text-xs sm:text-body-sm text-gray-500">
              Every payment and reversal, newest first.
            </p>
          </div>

          {ledgerLoading ? (
            <TableSkeleton rows={5} cols={5} />
          ) : ledger.length === 0 ? (
            <div className="p-8">
              <EmptyState icon={Receipt} title="No payments recorded" description="Payments appear here as you settle months." />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left">
                <thead>
                  <tr className="text-xs sm:text-label-sm uppercase tracking-wide text-gray-500 border-b border-emerald-100">
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Employee</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Month</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Amount</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold hidden sm:table-cell">Mode</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold hidden md:table-cell">Reference</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold hidden lg:table-cell">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-emerald-100/50">
                  {ledger.map((row, i) => {
                    const amount = row.amount ?? 0;
                    const isReversal = amount < 0;
                    return (
                      <tr
                        key={row._id ?? i}
                        className="text-xs sm:text-body-sm text-gray-800 hover:bg-emerald-50/50 transition-colors duration-200"
                      >
                        <td className="px-3 sm:px-4 py-2 sm:py-3">
                          <div className="flex items-center gap-2">
                            <Avatar name={refLabel(row.employee, "?")} size={26} />
                            <span className="truncate text-xs font-medium text-gray-800">
                              {refLabel(row.employee, "Unknown")}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 sm:px-4 py-2 sm:py-3 text-gray-600">{formatMonth(row.month)}</td>
                        <td className="px-3 sm:px-4 py-2 sm:py-3">
                          <span className={isReversal ? "font-semibold text-red-600" : "font-semibold text-emerald-700"}>
                            {isReversal ? "−" : ""}
                            {formatMoney(Math.abs(amount))}
                          </span>
                          {isReversal && <span className="ml-1 text-xs text-gray-500">reversed</span>}
                          {row.status && (
                            <span
                              className={`ml-2 hidden rounded-full px-2 py-0.5 text-[10px] font-semibold lg:inline ${
                                PAYROLL_STATUS_STYLES[row.status] ?? "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {PAYROLL_STATUS_LABELS[row.status] ?? row.status}
                            </span>
                          )}
                        </td>
                        <td className="px-3 sm:px-4 py-2 sm:py-3 hidden sm:table-cell text-gray-600">
                          {PAYMENT_MODE_LABELS[row.mode] ?? row.mode ?? "—"}
                        </td>
                        <td className="px-3 sm:px-4 py-2 sm:py-3 hidden md:table-cell text-gray-500">
                          {row.reference || "—"}
                        </td>
                        <td className="px-3 sm:px-4 py-2 sm:py-3 hidden lg:table-cell text-gray-500">
                          {formatDateTime(row.at ?? row.paidAt ?? row.createdAt) || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!ledgerLoading && ledger.length > 0 && (
            <div className="px-3 sm:px-4 border-t border-emerald-100/60 pt-3 sm:pt-4">
              <Pagination
                page={pagination.page}
                pages={pagination.pages}
                total={pagination.total}
                limit={pagination.limit}
                onPageChange={setPage}
              />
            </div>
          )}
        </Card>

        <p className="mt-4 text-xs text-gray-500">
          This covers months that have been processed. A month nobody has touched yet carries no row
          here — its liability shows in the{" "}
          <Link to="/payroll" className="font-semibold text-emerald-700 underline">
            payroll sheet
          </Link>
          , which is the number to look at before payday.
        </p>
      </div>
    </div>
  );
}
